import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TeacherAttendanceView from './TeacherAttendanceView';
import TeacherAssignmentsView from './TeacherAssignmentsView';
import TeacherEventsView from './TeacherEventsView';
import TeacherPrepView from './TeacherPrepView';
import TeacherChatView from './TeacherChatView';
import TeacherSubjectView from './TeacherSubjectView';
import TeacherTimetableView from './TeacherTimetableView';
import TeacherOnlineClassesView from './TeacherOnlineClassesView';
import TeacherLearningMaterialsView from './TeacherLearningMaterialsView';
import TeacherGradeRequestsView from './TeacherGradeRequestsView';
import TeacherClassesView from './TeacherClassesView';
import TeacherEngagementView from './TeacherEngagementView';
import TeacherGradingView from './TeacherGradingView';
import SubjectTeacherClassBrowser from './SubjectTeacherClassBrowser';
import StudentBehaviorView from './StudentBehaviorView';
import { useSchoolName } from './useSchoolName';
import './TeacherDashboard.css';

function TeacherDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [teacherData, setTeacherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubjectView, setShowSubjectView] = useState(false);
  const schoolName = useSchoolName(teacherData?.schoolId);

  const isSubjectTeacher = teacherData?.teacherType === 'subject';

  const teacherTabs = isSubjectTeacher ? [
    { id: 0, title: 'Dashboard', icon: '🏠' },
    { id: 1, title: 'Attendance', icon: '✅' },
    { id: 2, title: 'Assignments', icon: '📝' },
    { id: 3, title: 'Subject Classes', icon: '📖' },
    { id: 4, title: 'Online Classes', icon: '📹' },
    { id: 5, title: 'Timetable', icon: '�️' },
    { id: 6, title: 'Teacher Prep', icon: '📚' },
    { id: 7, title: 'Materials', icon: '�' },
    { id: 8, title: 'Events', icon: '�' },
    { id: 9, title: 'Grade Approvals', icon: '�️' },
    { id: 10, title: 'Parent Chat', icon: '�' },
    { id: 11, title: 'Profile', icon: '👤' },
    { id: 12, title: 'Behavior', icon: '📋' },
    { id: 14, title: 'Engagement', icon: '📊' },
    { id: 15, title: 'Grading', icon: '✏️' },
  ] : [
    { id: 0, title: 'Dashboard', icon: '🏠' },
    { id: 1, title: 'Attendance', icon: '✅' },
    { id: 2, title: 'Assignments', icon: '📝' },
    { id: 3, title: 'Online Classes', icon: '📹' },
    { id: 4, title: 'Timetable', icon: '🗓️' },
    { id: 5, title: 'Teacher Prep', icon: '📚' },
    { id: 6, title: 'Materials', icon: '�' },
    { id: 7, title: 'Events', icon: '�' },
    { id: 8, title: 'Grade Approvals', icon: '�️' },
    { id: 9, title: 'Parent Chat', icon: '�' },
    { id: 10, title: 'Profile', icon: '👤' },
    { id: 12, title: 'Behavior', icon: '📋' },
    { id: 13, title: 'My Classes', icon: '🏫' },
    { id: 14, title: 'Engagement', icon: '📊' },
    { id: 15, title: 'Grading', icon: '✏️' },
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          // Try to load from users collection first
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};
          
          // If not in users, try teachers collection
          let teacherName = userData.username || user.displayName;
          if (!teacherName) {
            const teacherDoc = await getDoc(doc(db, 'teachers', user.uid));
            const teacherData = teacherDoc.exists() ? teacherDoc.data() : {};
            teacherName = teacherData.fullName || user.displayName;
          }
          
          setTeacherData({
            uid: user.uid,
            email: user.email,
            displayName: teacherName || user.email.split('@')[0],
            role: 'teacher',
            teacherType: userData.teacherType || 'regular',
            specialtySubject: userData.specialtySubject || null,
            schoolId: userData.schoolId || null
          });
        } catch (error) {
          console.error('Error loading teacher data:', error);
          setTeacherData({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0],
            role: 'teacher',
            teacherType: 'regular',
            specialtySubject: null,
            schoolId: null
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="teacher-dashboard">
      <header className="dashboard-header">
        <div className="header-brand">
          <img src="/logo512.png" alt="KN Education Portal logo" className="header-logo" />
          <div>
            <h1>KN Education Portal</h1>
            {schoolName && <span className="header-school">{schoolName}</span>}
          </div>
        </div>
        <div className="header-actions">
          <span className="teacher-name">{teacherData?.displayName || 'Teacher'}</span>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-layout">
        <nav className="sidebar">
          {teacherTabs.map((tab) => (
            <button
              key={tab.id}
              className={`nav-button ${selectedTab === tab.id ? 'active' : ''}`}
              onClick={() => { setShowSubjectView(false); setSelectedTab(tab.id); }}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.title}</span>
            </button>
          ))}
        </nav>

        <main className="main-content">
          {showSubjectView ? (
            <TeacherSubjectView
              teacherData={teacherData}
              onClose={() => setShowSubjectView(false)}
            />
          ) : (
            <>
              {selectedTab === 0 && <TeacherDashboardTab teacherData={teacherData} setShowSubjectView={setShowSubjectView} />}
              {selectedTab === 1 && <TeacherAttendanceTab teacherData={teacherData} />}
              {selectedTab === 2 && <TeacherAssignmentsTab teacherData={teacherData} />}
              {selectedTab === 3 && (isSubjectTeacher
                ? <SubjectTeacherClassBrowser teacherData={teacherData} />
                : <TeacherOnlineClassesView teacherId={teacherData?.uid} schoolId={teacherData?.schoolId} />)}
              {selectedTab === 4 && (isSubjectTeacher
                ? <TeacherOnlineClassesView teacherId={teacherData?.uid} schoolId={teacherData?.schoolId} />
                : <TeacherTimetableTab teacherData={teacherData} />)}
              {selectedTab === 5 && (isSubjectTeacher
                ? <TeacherTimetableTab teacherData={teacherData} />
                : <TeacherPrepTab teacherData={teacherData} />)}
              {selectedTab === 6 && (isSubjectTeacher
                ? <TeacherPrepTab teacherData={teacherData} />
                : <TeacherLearningMaterialsView teacherData={teacherData} />)}
              {selectedTab === 7 && (isSubjectTeacher
                ? <TeacherLearningMaterialsView teacherData={teacherData} />
                : <TeacherEventsTab teacherData={teacherData} />)}
              {selectedTab === 8 && (isSubjectTeacher
                ? <TeacherEventsTab teacherData={teacherData} />
                : <TeacherGradeRequestsView teacherData={teacherData} />)}
              {selectedTab === 9 && (isSubjectTeacher
                ? <TeacherGradeRequestsView teacherData={teacherData} />
                : <TeacherChatTab teacherData={teacherData} />)}
              {selectedTab === 10 && (isSubjectTeacher
                ? <TeacherChatTab teacherData={teacherData} />
                : <TeacherProfileTab teacherData={teacherData} setShowSubjectView={setShowSubjectView} />)}
              {selectedTab === 11 && isSubjectTeacher && <TeacherProfileTab teacherData={teacherData} setShowSubjectView={setShowSubjectView} />}
              {selectedTab === 12 && <StudentBehaviorView user={teacherData} mode="teacher" />}
              {selectedTab === 13 && <TeacherClassesView teacherData={teacherData} />}
              {selectedTab === 14 && <TeacherEngagementView teacherData={teacherData} />}
              {selectedTab === 15 && <TeacherGradingView teacherData={teacherData} />}
            </>
          )}
        </main>
      </div>

      {/* Mobile-only content area — outside dashboard-layout to avoid flex height collapse */}
      <main className="mobile-main-content">
        {showSubjectView ? (
          <TeacherSubjectView
            teacherData={teacherData}
            onClose={() => setShowSubjectView(false)}
          />
        ) : (
          <>
            {selectedTab === 0 && <TeacherDashboardTab teacherData={teacherData} setShowSubjectView={setShowSubjectView} />}
            {selectedTab === 1 && <TeacherAttendanceTab teacherData={teacherData} />}
            {selectedTab === 2 && <TeacherAssignmentsTab teacherData={teacherData} />}
            {selectedTab === 3 && (isSubjectTeacher
              ? <SubjectTeacherClassBrowser teacherData={teacherData} />
              : <TeacherOnlineClassesView teacherId={teacherData?.uid} schoolId={teacherData?.schoolId} />)}
            {selectedTab === 4 && (isSubjectTeacher
              ? <TeacherOnlineClassesView teacherId={teacherData?.uid} schoolId={teacherData?.schoolId} />
              : <TeacherTimetableTab teacherData={teacherData} />)}
            {selectedTab === 5 && (isSubjectTeacher
              ? <TeacherTimetableTab teacherData={teacherData} />
              : <TeacherPrepTab teacherData={teacherData} />)}
            {selectedTab === 6 && (isSubjectTeacher
              ? <TeacherPrepTab teacherData={teacherData} />
              : <TeacherLearningMaterialsView teacherData={teacherData} />)}
            {selectedTab === 7 && (isSubjectTeacher
              ? <TeacherLearningMaterialsView teacherData={teacherData} />
              : <TeacherEventsTab teacherData={teacherData} />)}
            {selectedTab === 8 && (isSubjectTeacher
              ? <TeacherEventsTab teacherData={teacherData} />
              : <TeacherGradeRequestsView teacherData={teacherData} />)}
            {selectedTab === 9 && (isSubjectTeacher
              ? <TeacherGradeRequestsView teacherData={teacherData} />
              : <TeacherChatTab teacherData={teacherData} />)}
            {selectedTab === 10 && (isSubjectTeacher
              ? <TeacherChatTab teacherData={teacherData} />
              : <TeacherProfileTab teacherData={teacherData} setShowSubjectView={setShowSubjectView} />)}
            {selectedTab === 11 && isSubjectTeacher && <TeacherProfileTab teacherData={teacherData} setShowSubjectView={setShowSubjectView} />}
            {selectedTab === 12 && <StudentBehaviorView user={teacherData} mode="teacher" />}
            {selectedTab === 13 && <TeacherClassesView teacherData={teacherData} />}
            {selectedTab === 14 && <TeacherEngagementView teacherData={teacherData} />}
            {selectedTab === 15 && <TeacherGradingView teacherData={teacherData} />}
          </>
        )}
      </main>

      {/* Mobile Bottom Nav - scrollable for all tabs */}
      <nav className="mobile-bottom-nav">
        {teacherTabs.map((tab) => (
          <button
            key={tab.id}
            className={`mobile-nav-btn ${selectedTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              console.log('🔥 Mobile nav clicked:', tab.title, tab.id);
              setShowSubjectView(false);
              setSelectedTab(tab.id);
            }}
          >
            <span className="mobile-nav-icon">{tab.icon}</span>
            <span className="mobile-nav-label">{tab.title}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function TeacherDashboardTab({ teacherData, setShowSubjectView }) {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }

    console.log('🔥 TeacherDashboard: Loading data for teacher', teacherData.uid);
    console.log('🔥 TeacherDashboard: Teacher data', teacherData);

    try {
      // First, load ALL classes to see what's in the database
      console.log('🔥 TeacherDashboard: Loading ALL classes for debugging');
      const allClassesQuery = query(collection(db, 'classes'));
      const allClassesSnapshot = await getDocs(allClassesQuery);
      const allClassesData = allClassesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherDashboard: ALL classes in database', allClassesData.length, allClassesData);
      console.log('🔥 TeacherDashboard: Class teacherIds', allClassesData.map(c => ({ id: c.id, name: c.name, teacherId: c.teacherId })));

      // Load classes for this teacher
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const classesSnapshot = await getDocs(classesQuery);
      const classesData = classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherDashboard: Classes loaded for teacher', classesData.length, classesData);
      setClasses(classesData);

      const teacherClassIds = classesData.map(c => c.id);
      console.log('🔥 TeacherDashboard: Teacher class IDs', teacherClassIds);

      // Load students from each class's students subcollection
      if (teacherClassIds.length > 0) {
        const studentsData = [];
        for (const classId of teacherClassIds) {
          console.log('🔥 TeacherDashboard: Loading students for class', classId);
          try {
            const studentsQuery = query(
              collection(db, 'classes', classId, 'students'),
              where('isActive', '==', true)
            );
            const studentsSnapshot = await getDocs(studentsQuery);
            console.log('🔥 TeacherDashboard: Students snapshot for class', classId, 'count:', studentsSnapshot.docs.length);
            studentsSnapshot.docs.forEach(doc => {
              const studentData = { id: doc.id, ...doc.data() };
              console.log('🔥 TeacherDashboard: Student data', studentData);
              studentsData.push(studentData);
            });
          } catch (error) {
            console.error('🔥 TeacherDashboard: Error loading students for class', classId, error);
          }
        }
        console.log('🔥 TeacherDashboard: Total students loaded', studentsData.length, studentsData);
        setStudents(studentsData);

        // Load today's attendance
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        // Format to match iOS timezone format (e.g., 2026-06-29T00:00:00-04:00)
        const offset = startOfDay.getTimezoneOffset();
        const offsetHours = Math.abs(Math.floor(offset / 60));
        const offsetMinutes = Math.abs(offset % 60);
        const offsetSign = offset <= 0 ? '+' : '-';
        const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
        const today = `${startOfDay.getFullYear()}-${String(startOfDay.getMonth() + 1).padStart(2, '0')}-${String(startOfDay.getDate()).padStart(2, '0')}T00:00:00${offsetStr}`;
        console.log('🔥 TeacherDashboard: Loading attendance for', today);
        const attendancePromises = teacherClassIds.map(classId =>
          getDocs(query(
            collection(db, 'classes', classId, 'attendanceRecords'),
            where('date', '==', today)
          ))
        );
        const attendanceSnapshots = await Promise.all(attendancePromises);
        const allAttendance = [];
        attendanceSnapshots.forEach(snapshot => {
          snapshot.docs.forEach(doc => allAttendance.push(doc.data()));
        });
        console.log('🔥 TeacherDashboard: Attendance loaded', allAttendance.length, allAttendance);
        setAttendanceRecords(allAttendance);

        // Load events
        const eventsQuery = query(collection(db, 'events'), where('isActive', '==', true));
        const eventsSnapshot = await getDocs(eventsQuery);
        const allEvents = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('🔥 TeacherDashboard: All events loaded', allEvents.length, allEvents);
        const filteredEvents = allEvents.filter(event => {
          if (!event.participatingClassIds) return false;
          return event.participatingClassIds.some(classId => teacherClassIds.includes(classId));
        });
        console.log('🔥 TeacherDashboard: Filtered events for teacher', filteredEvents.length, filteredEvents);
        setEvents(filteredEvents);

        // Load assignments from each class's assignments subcollection
        const assignmentsData = [];
        for (const classId of teacherClassIds) {
          const assignmentsQuery = query(
            collection(db, 'classes', classId, 'assignments')
          );
          const assignmentsSnapshot = await getDocs(assignmentsQuery);
          assignmentsSnapshot.docs.forEach(doc => {
            assignmentsData.push({ id: doc.id, ...doc.data() });
          });
        }
        console.log('🔥 TeacherDashboard: Assignments loaded', assignmentsData.length, assignmentsData);
        setAssignments(assignmentsData);

        // Load conversations for message count
        const teacherConversationsQuery = query(
          collection(db, 'teacherParentConversations'),
          where('teacherId', '==', teacherData.uid)
        );
        const teacherSnapshot = await getDocs(teacherConversationsQuery);
        const teacherConversations = teacherSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const adminConversationsQuery = query(
          collection(db, 'teacherParentConversations'),
          where('conversationType', '==', 'admin')
        );
        const adminSnapshot = await getDocs(adminConversationsQuery);
        const adminConversations = adminSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const allConversations = [...teacherConversations, ...adminConversations];
        console.log('🔥 TeacherDashboard: Conversations loaded', allConversations.length, allConversations);
        setConversations(allConversations);
      } else {
        console.log('🔥 TeacherDashboard: No classes found for teacher');
      }
    } catch (error) {
      console.error('🔥 TeacherDashboard: Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Calculate stats
  const todayAttendanceRate = attendanceRecords.length > 0
    ? (attendanceRecords.filter(r => r.status === 'Present').length / attendanceRecords.length * 100).toFixed(1)
    : '0.0';

  const upcomingEvents = events.filter(e => {
    const eventDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
    return eventDate >= new Date();
  });

  const pendingAssignments = assignments.filter(a => {
    const dueDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
    return dueDate >= new Date() && a.isActive !== false;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <h2>Welcome, {teacherData?.displayName || 'Teacher'}</h2>

      <div className="stats-section">
        <h3>Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🏫</div>
            <div className="stat-info">
              <h3>{classes.length}</h3>
              <p>Classes</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>{students.length}</h3>
              <p>Students</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">☀️</div>
            <div className="stat-info">
              <h3>{todayAttendanceRate}%</h3>
              <p>Attendance Today</p>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h3>Activity Today</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-info">
              <h3>{upcomingEvents.length}</h3>
              <p>Activities</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-info">
              <h3>{pendingAssignments.length}</h3>
              <p>Pending</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💬</div>
            <div className="stat-info">
              <h3>{conversations.length}</h3>
              <p>Messages</p>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h3>Subject Management</h3>
        <button className="action-button" onClick={() => setShowSubjectView(true)}>
          <span>📚</span>
          <span>Manage My Subjects</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
}

function TeacherAttendanceTab({ teacherData }) {
  return <TeacherAttendanceView teacherData={teacherData} />;
}

function TeacherAssignmentsTab({ teacherData }) {
  return <TeacherAssignmentsView teacherData={teacherData} />;
}

function TeacherPrepTab({ teacherData }) {
  return <TeacherPrepView teacherData={teacherData} />;
}

function TeacherEventsTab({ teacherData }) {
  return <TeacherEventsView teacherData={teacherData} />;
}

function TeacherChatTab({ teacherData }) {
  return <TeacherChatView teacherData={teacherData} />;
}

function TeacherTimetableTab({ teacherData }) {
  return <TeacherTimetableView teacherData={teacherData} />;
}

function TeacherProfileTab({ teacherData, setShowSubjectView }) {
  const [teacherClass, setTeacherClass] = useState(null);

  const loadTeacherClass = useCallback(async () => {
    if (!teacherData?.uid) return;
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      if (!snapshot.empty) {
        setTeacherClass({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
      }
    } catch (error) {
      console.error('Error loading teacher class:', error);
    }
  }, [teacherData]);

  useEffect(() => {
    loadTeacherClass();
  }, [loadTeacherClass]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="tab-content">
      <div className="profile-header">
        <div className="profile-photo">
          <span className="placeholder-photo">👤</span>
        </div>
        <div className="profile-name-section">
          <h2>{teacherData?.displayName || 'Teacher'}</h2>
          <p>Teacher{teacherClass ? ` · ${teacherClass.name}` : ''}</p>
          {teacherClass && <p style={{fontSize:'13px', color:'#666', margin:'2px 0 0'}}>{teacherClass.grade}</p>}
        </div>
      </div>

      <div className="profile-section">
        <h3>Teacher Information</h3>
        <div className="profile-info">
          <div className="profile-field">
            <label>Name</label>
            <p>{teacherData?.displayName || 'N/A'}</p>
          </div>
          <div className="profile-field">
            <label>Email</label>
            <p>{teacherData?.email || 'N/A'}</p>
          </div>
          <div className="profile-field">
            <label>Class</label>
            <p>{teacherClass ? teacherClass.name : 'Not assigned'}</p>
          </div>
          <div className="profile-field">
            <label>Grade</label>
            <p>{teacherClass ? `Grade ${teacherClass.grade}` : 'N/A'}</p>
          </div>
          <div className="profile-field">
            <label>Role</label>
            <p>Teacher</p>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h3>Subject Management</h3>
        <button className="action-button" onClick={() => setShowSubjectView(true)}>
          <span>📚</span>
          <span>Manage My Subjects</span>
          <span>→</span>
        </button>
      </div>

      <div className="profile-section danger">
        <h3>Account Settings</h3>
        <button className="logout-button" onClick={handleLogout}>
          <span>🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}

export default TeacherDashboard;
